import { setActiveArtifactId } from '@/redux/slices/aiChatSlice'
import {
  AIGeneratedForm,
  removeGeneratedForm,
  selectLatestGeneratedForm,
  setConversationMessageMarks,
  upsertGeneratedForm,
} from '@/redux/slices/formAIGenerationSlice'
import { getCurrentUserAIPointsUsageThunk } from '@/redux/slices/usageAndBalanceSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { RefObject, useEffect, useRef } from 'react'
import { AIConnectionRef } from '../aiConnection/AIConnection'
import createFormGenerationEventsHandlerFactory, { buildFormArtifact } from './createFormGenerationEventsHandlerFactory'
import { FormCustomEvents, FormGenerationClientCommand, FormStepEvents } from './types'

type AICreateFormEventsHandlerProps = {
  conversationId: string
  aiConnectionRef: React.RefObject<AIConnectionRef<
    FormStepEvents,
    FormCustomEvents,
    FormGenerationClientCommand
  > | null>
}

export default function AICreateFormEventsHandler({ conversationId, aiConnectionRef }: AICreateFormEventsHandlerProps) {
  const dispatch = useAppDispatch()

  const latestGeneratedFormRef = useRef<AIGeneratedForm | undefined>(undefined)

  useEffect(() => {
    const aiConnection = aiConnectionRef.current
    if (!aiConnection) return

    const eventsHandlerFactory = createFormGenerationEventsHandlerFactory({
      conversationId,
      getCurrentMessageId: () => aiConnection.currentMessageId,
      getLatestGeneratedForm: () => latestGeneratedFormRef.current,
      upsertGeneratedForm: form => dispatch(upsertGeneratedForm(form)),
      setSelectedFormId: id => dispatch(setActiveArtifactId({ conversationId, artifactId: id })),
      setConversationMessageMarks: marks => {
        const messageId = aiConnection.currentMessageId
        if (messageId) dispatch(setConversationMessageMarks({ conversationId, messageId, marks }))
      },
      removeGeneratedForm: formId => {
        dispatch(setActiveArtifactId({ conversationId, artifactId: undefined }))
        dispatch(removeGeneratedForm({ formId }))
      },
      reportGeneratedForm: form => {
        aiConnection.processor.perform('update_artifact', {
          conversation_id: aiConnection.processor.backendConversationId,
          artifact_id: form.id,
          artifact: buildFormArtifact(form),
        })
      },
      onStopped: () => {
        dispatch(getCurrentUserAIPointsUsageThunk())
      },
    })

    aiConnection.processor.registerEventsHandler(eventsHandlerFactory)

    return () => {
      aiConnection.processor.unregisterEventsHandler(eventsHandlerFactory)
    }
  }, [aiConnectionRef, conversationId, dispatch])

  return <SelectLatestGeneratedForm latestGeneratedFormRef={latestGeneratedFormRef} conversationId={conversationId} />
}

function SelectLatestGeneratedForm({
  latestGeneratedFormRef,
  conversationId,
}: {
  latestGeneratedFormRef: RefObject<AIGeneratedForm | undefined>
  conversationId: string
}) {
  const latestGeneratedForm = useAppSelector(state => selectLatestGeneratedForm(state, conversationId))
  latestGeneratedFormRef.current = latestGeneratedForm
  return null
}
